const productSubscriptions = class extends HTMLElement {
  constructor() {
    super()

    this.variantId = Number(this.dataset.selectedVariantId)
    this.subOptions = this.querySelectorAll('[data-subscription-option]')
    this.select = this.querySelector('[data-selling-plan-select]')
    this.activeType = [...this.subOptions].find(option => option.checked)?.value || 'subscribe'
    this.subContent = this.querySelector('[data-subscribe-content]')
    this.buyPrice = this.querySelector('[data-buy-price]')
    this.subPrice = this.querySelector('[data-sub-price]')
    this.input = this.querySelector('[data-selling-plan-input]')
    this.optionsJSON = JSON.parse(this.querySelector('[data-options-json]').innerHTML)
    this.oneTimeLabel = this.dataset.oneTimeLabel
  }

  connectedCallback() {
    window.addEventListener('variant:change', (event) => {
      this.variantChanged(event.detail.variant)
    })

    this.subOptions.forEach((option) => {
      option.addEventListener('change', () => {
        this.updateType(option.value)
      })
    })

    this.select.addEventListener('change', () => {
      this.updateSellingPlan()
    })

    const form = this.closest('form')
    if (form) {
      this._onFormSubmit = () => {
        this.syncPurchaseType(this.isOneTimeSelected() ? 'one-time' : 'subscribe')
      }
      form.addEventListener('submit', this._onFormSubmit, true)
    }

    // Ensure initial UI + hidden input state match the default checked radio.
    // (Theme settings can change which radio is checked in Liquid.)
    this.syncPurchaseType(this.activeType)
  }

  disconnectedCallback() {
    const form = this.closest('form')
    form?.removeEventListener('submit', this._onFormSubmit, true)
  }

  isOneTimeSelected() {
    return Boolean(this.querySelector('[data-subscription-option][value="one-time"]')?.checked)
  }

  syncPurchaseType(type) {
    this.activeType = type
    this.subContent?.classList.toggle('hidden', type === 'one-time')

    if (type === 'one-time') {
      this.disableSellingPlan()
      return
    }

    this.enableSellingPlan(this.select?.value)
  }

  updateType(value) {
    this.activeType = value
    this.subContent.classList.toggle('hidden', value === 'one-time')

    if (this.activeType === 'one-time') {
      this.disableSellingPlan()
      return
    }

    this.selectFirstAvailableOption()
  }

  disableSellingPlan() {
    if (!this.input) { return }

    this.input.value = ''
    this.input.disabled = true
    this.input.removeAttribute('name')
  }

  enableSellingPlan(planId) {
    if (!this.input || !planId) { return }

    this.input.disabled = false
    this.input.setAttribute('name', 'selling_plan')
    this.input.value = planId
  }

  selectFirstAvailableOption() {
    const options = this.select.querySelectorAll('option')
    const firstAvailableOption = [...options].find(option => !option.hidden && !option.disabled)
    if (!firstAvailableOption) { return }

    this.select.value = firstAvailableOption.value

    const changeEvent = new Event('change')
    this.select.dispatchEvent(changeEvent)
  }

  updateSellingPlan() {
    const options = this.select.querySelectorAll('option')
    const selectedOption = [...options].find(option => option.value === this.select.value)
    if (!selectedOption) { return }

    this.subPrice.innerHTML = this.buildPrice(
      Number(selectedOption.dataset.variantPrice),
      Number(selectedOption.dataset.variantCompareAtPrice)
    )

    if (this.activeType === 'one-time') { return }

    this.enableSellingPlan(this.select.value)
  }

  variantChanged(variant) {
    this.variantId = variant.id
    this.buildOptions()
    this.buyPrice.innerHTML = this.buildPrice(variant.price, variant.compare_at_price)

    // Refresh frequency options for the new variant, but do not re-attach a
    // selling plan when the shopper explicitly chose one-time.
    if (this.activeType === 'one-time') {
      const firstAvailableOption = [...this.select.querySelectorAll('option')]
        .find(option => !option.hidden && !option.disabled)

      if (firstAvailableOption) {
        this.select.value = firstAvailableOption.value
        this.subPrice.innerHTML = this.buildPrice(
          Number(firstAvailableOption.dataset.variantPrice),
          Number(firstAvailableOption.dataset.variantCompareAtPrice)
        )
      }

      this.disableSellingPlan()
      return
    }

    this.selectFirstAvailableOption()
  }

  buildOptions() {
    const data = this.optionsJSON[this.variantId]
    const optionEls = data.map((option) => {
      return `
        <option
          value="${option.id}"
          data-variant-price="${option.price}"
          data-variant-compare-at-price="${option.compare_price}"
        >
          ${option.name}
        </option>
      `
    })

    this.select.innerHTML = `
      ${optionEls.join('')}
    `
  }

  buildPrice(price, comparePrice) {
    if (!comparePrice || price >= comparePrice) {
      return `
        <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      `
    }
    return `
      <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      <s class="text-subdued">${this.intToMoney(comparePrice)}</s>
    `
  }

  intToMoney(price) {
    return `$${(price / 100).toFixed(2)}`
  }
}

if (typeof window.customElements.get('product-subscriptions') === 'undefined') {
  window.customElements.define('product-subscriptions', productSubscriptions)
}
